import type * as React from "react";
import {
  Focusable,
  Link,
  type PressEvent,
  Pressable,
  Text,
  useContextProps,
} from "react-aria-components";
import { icon } from "../icon.css.ts";
import { User01 } from "../icons/line.tsx";
import { abbreviateName } from "../utils/name.ts";
import useTextFit from "../utils/useTextFit.ts";
import {
  avatarImage,
  baseAvatarStyle,
  placeholderTextStyle,
} from "./Avatar.css.ts";
import { Tooltip } from "./Tooltip.tsx";
import {
  AvatarContext,
  type AvatarProps,
  type InternalAvatarProps,
  type Size,
} from "./shared/AvatarContext.ts";

const AutoTextFit: React.FC<{
  children: string;
  size: AvatarProps["size"];
}> = ({ size, children }) => {
  const { textRef, containerRef } = useTextFit(children, 6, 16, 10, 0.5);

  return (
    <Text ref={containerRef} className={placeholderTextStyle({ size })}>
      <span ref={textRef}>{children}</span>
    </Text>
  );
};

const defaultSize: Size = "m";

interface AvatarImageProps
  extends
    Pick<AvatarProps, "size" | "href" | "onPress" | "active">,
    InternalAvatarProps {
  isIcon?: boolean;
  isInteractive: boolean;
  tooltip: boolean;
  showBorder?: boolean;
  children: React.ReactNode;
  ref?: React.Ref<HTMLDivElement | HTMLAnchorElement>;
}

const AvatarImage: React.FC<AvatarImageProps> = ({
  children,
  size = defaultSize,
  isIcon = false,
  insideInputBuilder = false,
  tooltip,
  href,
  onPress,
  active,
  isInteractive,
  showBorder,
  ref,
}) => {
  const className = baseAvatarStyle({
    size,
    isIcon,
    isInteractive,
    active,
    insideInputBuilder,
    showBorder,
  });

  if (href != null) {
    return (
      <Link
        ref={ref as React.Ref<HTMLAnchorElement>}
        onPress={onPress}
        href={href}
        className={className}
      >
        {children}
      </Link>
    );
  }

  if (!tooltip && onPress == null) {
    return <div className={className}>{children}</div>;
  }

  if (onPress == null) {
    return (
      <Focusable ref={ref}>
        <div
          // need to give it an interactive role
          role="treeitem"
          className={className}
        >
          {children}
        </div>
      </Focusable>
    );
  }

  return (
    <Pressable ref={ref} onPress={onPress}>
      <div
        // need to give it an interactive role
        role="treeitem"
        className={className}
      >
        {children}
      </div>
    </Pressable>
  );
};

interface AvatarTooltipProps extends Pick<
  AvatarProps,
  "size" | "tooltip" | "name" | "href" | "onPress" | "active"
> {
  isIcon?: true;
  isInteractive: boolean;
  children: React.ReactNode;
  ref?: React.Ref<HTMLDivElement>;
}

const AvatarTooltip: React.FC<AvatarTooltipProps> = ({
  children,
  tooltip,
  name = "",
  isIcon,
  ref,
  ...props
}) => {
  const text = typeof tooltip === "string" ? tooltip : name;

  const hasTooltip = tooltip !== false && text.length > 0;
  const avatarImage = (
    <AvatarImage tooltip={hasTooltip} {...props} ref={ref} isIcon={isIcon}>
      {children}
    </AvatarImage>
  );

  if (!hasTooltip) {
    return avatarImage;
  }

  return <Tooltip text={text}>{avatarImage}</Tooltip>;
};

const iconSizeMapping: Record<
  Size,
  keyof typeof icon.classNames.variants.size
> = {
  min: "min",
  tiny: "xs",
  xxs: "s",
  xs: "s",
  s: "m",
  m: "l",
  l: "l",
  max: "l",
};

/**
 * Displays a user's profile image, initials from their name, or a placeholder icon.
 * @see AvatarGroup for displaying multiple avatars together
 */
export const Avatar: React.FC<
  AvatarProps & { ref?: React.Ref<HTMLDivElement> }
> = ({ ref: baseRef, ...baseProps }) => {
  // eslint-disable-next-line @eslint-react/static-components
  const [combinedProps, ref] = useContextProps(
    baseProps,
    baseRef,
    AvatarContext,
  );

  const {
    src,
    size = defaultSize,
    alt,
    icon: Icon,
    isInteractive: isInteractiveProp,
    ...props
  } = combinedProps;

  const isInteractive =
    isInteractiveProp ?? (props.href != null || props.onPress != null);

  if (src != null) {
    return (
      <AvatarTooltip
        ref={ref}
        {...props}
        size={size}
        isInteractive={isInteractive}
      >
        <img className={avatarImage} src={src} alt={alt} fetchPriority="high" />
      </AvatarTooltip>
    );
  }

  if (Icon != null) {
    return (
      <AvatarTooltip
        ref={ref}
        {...props}
        size={size}
        isInteractive={isInteractive}
        isIcon={true}
      >
        {/* eslint-disable-next-line @eslint-react/static-components */}
        <Icon
          className={icon({ size: iconSizeMapping[size] })}
          aria-label={alt}
        />
      </AvatarTooltip>
    );
  }

  if (props.name != null) {
    const abbreviated = abbreviateName(props.name);
    // For smallest size, only show first character
    const displayText = size === "min" ? abbreviated[0] : abbreviated;
    return (
      <AvatarTooltip
        ref={ref}
        {...props}
        size={size}
        isInteractive={isInteractive}
      >
        <div className={placeholderTextStyle({ size })} aria-label={alt}>
          {displayText}
        </div>
      </AvatarTooltip>
    );
  }

  return (
    <AvatarImage
      ref={ref}
      {...props}
      size={size}
      isInteractive={isInteractive}
      isIcon={true}
      tooltip={false}
    >
      <User01
        className={icon({ size: iconSizeMapping[size] })}
        aria-label={alt}
      />
    </AvatarImage>
  );
};

export type AvatarSize = Size;

interface CounterProps extends Pick<AvatarProps, "size" | "tooltip"> {
  count: number;
  onPress?: (event: PressEvent) => void;
}

export const Counter: React.FC<CounterProps> = ({
  size,
  tooltip,
  count,
  onPress,
}) => (
  <AvatarTooltip
    tooltip={tooltip}
    size={size}
    onPress={onPress}
    isInteractive={onPress != null}
  >
    <AutoTextFit size={size}>{count > 99 ? "99+" : `+${count}`}</AutoTextFit>
  </AvatarTooltip>
);
